import { Link, useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Box, Typography, Button, Breadcrumbs, Chip, Icon } from '@glplus/glpweb-dsl';
import type { IconName } from '@glplus/glpweb-dsl';
import { BORDER, HEADING, MUTED, ORANGE_INK, PageTitle } from './parts';

/**
 * P4 — Add products (Figma `fVTA2OnSEz4UOdTeoXeByH` 58:231).
 *
 * Every way of adding products asks for the same details and ends in the same
 * place: live straight away, or hidden with a question when we need to check
 * something. This page is where the vendor picks the way that suits them.
 */

interface Way {
  key: 'spreadsheet' | 'oneAtATime' | 'pos' | 'ownSystem';
  icon: IconName;
  to: string;
  primary?: boolean;
}

const WAYS: Way[] = [
  { key: 'spreadsheet', icon: 'listAlt', to: '/products/add/import', primary: true },
  { key: 'oneAtATime', icon: 'check', to: '/products/add/new' },
  { key: 'pos', icon: 'creditCard', to: '/help' },
  { key: 'ownSystem', icon: 'devices', to: '/help' },
];

export default function AddProducts() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    <Box>
      <Breadcrumbs
        ariaLabel={t('products.detail.breadcrumbAria')}
        linkComponent={Link}
        items={[
          { label: t('nav.products'), to: '/products' },
          { label: t('products.add.title'), ariaCurrent: true },
        ]}
      />
      <Box sx={{ mt: 2 }}>
        <PageTitle title={t('products.add.title')} subtitle={t('products.add.subtitle')} />
      </Box>

      <Box sx={{ mt: 3, display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
        {WAYS.map((way) => (
          <Box
            key={way.key}
            data-testid={`add-products-${way.key}`}
            sx={{ p: 3, bgcolor: '#fff', border: way.primary ? `2px solid #F67A1A` : `1px solid ${BORDER}`, borderRadius: '10px', display: 'flex', flexDirection: 'column', gap: 1.5 }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
              <Box aria-hidden sx={{ width: 44, height: 44, borderRadius: '8px', bgcolor: '#FEF1E7', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Icon name={way.icon} size={22} color={ORANGE_INK} />
              </Box>
              <Typography component="h2" sx={{ flex: 1, minWidth: 160, fontSize: 21, fontWeight: 600, color: HEADING }}>
                {t(`products.add.ways.${way.key}.title`)}
              </Typography>
              <Chip label={t(`products.add.ways.${way.key}.tag`)} size="small" backgroundColor="#F1F2F4" textColor="#3C4650" fontSize={12} fontWeight={700} />
            </Box>
            <Typography sx={{ fontSize: 15, color: '#343434' }}>{t(`products.add.ways.${way.key}.body`)}</Typography>
            <Box>
              <Button
                ariaLabel={t(`products.add.ways.${way.key}.cta`)}
                buttonText={t(`products.add.ways.${way.key}.cta`)}
                variant={way.primary ? 'contained' : 'outlinedGreen'}
                color={way.primary ? 'primary' : undefined}
                onClick={() => void navigate(way.to)}
                dataTestId={`add-products-${way.key}-cta`}
              />
            </Box>
          </Box>
        ))}
      </Box>

      <Box sx={{ mt: 3, p: 3, bgcolor: '#EAF2FB', borderRadius: '10px' }}>
        <Typography component="h2" sx={{ fontSize: 17, fontWeight: 700, color: HEADING }}>
          {t('products.add.whatWeAsk.title')}
        </Typography>
        <Typography sx={{ mt: 0.75, fontSize: 15, color: MUTED }}>{t('products.add.whatWeAsk.body')}</Typography>
      </Box>
    </Box>
  );
}
